import { Fragment, useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2Icon } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import {
    useBackOfficeScanners,
    useSetScannerTopEdge,
    type BackOfficeScanner,
} from '@/services/scanner.service';
import { useSetScannerResolution } from '@/services/field.service';
import { hasCrop, scannerCrop } from '@/lib/crop';
import { BASE } from '../../basePath';
import BackofficePage, {
    DataCard,
    DataTable,
    TH,
    TR,
    TD,
} from '../../components/BackofficePage';
import AddScannerDialog from './AddScannerDialog';
import { groupByProject, ProjectGroupRow } from './projectGroups';
import { saveErrorMessage } from './saveError';

const MUTED = 'text-[#6b7480]';
const CONTROL =
    'bg-[#0f141a] border border-[#1e242a] rounded px-1.5 py-0.5 text-xs text-[#d8dde2] focus:outline-none focus:border-[#74c69d] disabled:opacity-50';
// What the monitoring page offers too.
const DPI_CHOICES = [300, 600, 1200];

function Pending({ error, pending }: { error: unknown; pending: boolean }) {
    if (pending)
        return <Loader2Icon className="h-3 w-3 animate-spin text-[#6b7480]" />;
    if (error)
        return (
            <span className="text-[#d9534f]" title={saveErrorMessage(error)}>
                save failed
            </span>
        );
    return null;
}

/** Written to the DB and to the scanner's config on its hub. */
function ResolutionCell({ scanner }: { scanner: BackOfficeScanner }) {
    const save = useSetScannerResolution();
    const current = scanner.resolutionDpi;
    return (
        <span className="inline-flex items-center gap-1.5">
            <Pending error={save.error} pending={save.isPending} />
            <select
                value={current}
                disabled={save.isPending}
                onChange={(e) =>
                    save.mutate({
                        serialNumber: scanner.serialNumber,
                        resolutionDpi: Number(e.target.value),
                    })
                }
                title="Acquisition resolution — written to the scanner's config on its hub"
                className={CONTROL}
            >
                {/* One read back from the hub may be on none of the three. */}
                {!DPI_CHOICES.includes(current) && (
                    <option value={current}>{current} dpi</option>
                )}
                {DPI_CHOICES.map((dpi) => (
                    <option key={dpi} value={dpi}>
                        {dpi} dpi
                    </option>
                ))}
            </select>
        </span>
    );
}

/** Saved on Enter or on leaving the field, when it changed. */
function TopEdgeCell({ scanner }: { scanner: BackOfficeScanner }) {
    const save = useSetScannerTopEdge();
    const [draft, setDraft] = useState(String(scanner.topEdgeCm));
    const value = /^\d+$/.test(draft.trim()) ? Number(draft.trim()) : null;
    const invalid = value === null || value > 100;

    const commit = () => {
        if (invalid) {
            setDraft(String(scanner.topEdgeCm));
            return;
        }
        if (value === scanner.topEdgeCm) return;
        save.mutate({ serialNumber: scanner.serialNumber, topEdgeCm: value });
    };

    return (
        <span className="inline-flex items-center gap-1.5">
            <Pending error={save.error} pending={save.isPending} />
            <input
                value={draft}
                inputMode="numeric"
                onChange={(e) => setDraft(e.target.value)}
                onBlur={commit}
                onKeyDown={(e) => {
                    if (e.key === 'Enter') e.currentTarget.blur();
                    else if (e.key === 'Escape') {
                        setDraft(String(scanner.topEdgeCm));
                        e.currentTarget.blur();
                    }
                }}
                title="From the top of the scanner's shell to the top of its glass — feeds the viewer's depth scale"
                className={`${CONTROL} w-12 text-right tabular-nums ${
                    invalid ? 'border-red-500' : ''
                }`}
            />
            <span className={MUTED}>cm</span>
        </span>
    );
}

export default function BackOfficeScanners() {
    const { data, isLoading, error } = useBackOfficeScanners();
    const [query, setQuery] = useState('');

    if (isLoading)
        return (
            <BackofficePage title="Scanners" description="Loading…">
                <Skeleton className="w-full h-[400px] bg-[#1e242a]" />
            </BackofficePage>
        );
    if (error)
        return (
            <BackofficePage title="Scanners" description="Error">
                <div className="text-red-400 text-sm">{error.toString()}</div>
            </BackofficePage>
        );

    const q = query.trim().toLowerCase();
    const groups = groupByProject(
        (data ?? []).filter(
            (r) =>
                !q ||
                r.serialNumber.toLowerCase().includes(q) ||
                (r.projectName ?? '').toLowerCase().includes(q) ||
                (r.sampleName ?? '').toLowerCase().includes(q) ||
                (r.hubId ?? '').toLowerCase().includes(q),
        ),
        (r) => ({ id: r.projectId, name: r.projectName ?? r.projectId }),
    );

    return (
        <BackofficePage title="Scanners" actions={<AddScannerDialog />} wide>
            <div className="mb-4">
                <input
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Filter by serial number, sample, project or hub…"
                    className="w-full max-w-sm bg-[#0f141a] border border-[#1e242a] rounded-md px-3 py-2 text-sm text-[#e5e7e3] placeholder-[#6b7480] focus:outline-none focus:border-[#2d6a4f]"
                />
            </div>
            <DataCard>
                <DataTable>
                    <thead className="bg-[#0f141a]">
                        <tr>
                            <TH>Scanner</TH>
                            <TH>Sample</TH>
                            <TH>Hub</TH>
                            <TH align="right">Resolution</TH>
                            <TH align="right">Acquisitions</TH>
                            <TH align="right">Top edge</TH>
                            <TH align="center">
                                Crop, mm (top / bottom / left / right)
                            </TH>
                        </tr>
                    </thead>
                    <tbody>
                        {groups.map((group) => (
                            <Fragment key={group.id}>
                                <ProjectGroupRow
                                    name={group.name}
                                    count={group.rows.length}
                                    colSpan={7}
                                />
                                {group.rows.map((r) => (
                                    <ScannerRow key={r.serialNumber} r={r} />
                                ))}
                            </Fragment>
                        ))}
                        {groups.length === 0 && (
                            <tr>
                                <td
                                    colSpan={7}
                                    className={`px-4 py-10 text-center text-sm ${MUTED}`}
                                >
                                    No scanner.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </DataTable>
            </DataCard>
        </BackofficePage>
    );
}

function ScannerRow({ r }: { r: BackOfficeScanner }) {
    const crop = scannerCrop(r);
    return (
        <TR>
            <TD mono className="whitespace-nowrap">
                <span className="font-semibold text-white">
                    {r.serialNumber}
                </span>
            </TD>
            <TD>{r.sampleName ?? <span className={MUTED}>Not bound</span>}</TD>
            <TD mono className="whitespace-nowrap">
                {r.hubId ?? <span className={MUTED}>—</span>}
            </TD>
            <TD align="right" className="whitespace-nowrap">
                <ResolutionCell scanner={r} />
            </TD>
            <TD align="right">{r.acquisitionCount}</TD>
            <TD align="right" className="whitespace-nowrap">
                {/* Keyed on the value, so a save
                    elsewhere resets the draft. */}
                <TopEdgeCell key={r.topEdgeCm} scanner={r} />
            </TD>
            <TD align="center" mono className="whitespace-nowrap">
                {/* The way into the crop editor. */}
                <Link
                    to={`${BASE}/backOffice/scanners/${encodeURIComponent(r.serialNumber)}`}
                    className="text-[#74c69d] hover:underline"
                >
                    {hasCrop(crop)
                        ? `${crop.top} / ${crop.bottom} / ${crop.left} / ${crop.right}`
                        : 'None'}
                </Link>
            </TD>
        </TR>
    );
}
